import { Ellipsis } from "@visiblethread/react-components";
import clsx from "clsx";
import Flex, { ALIGN_CLASS, GAP_CLASS } from "components/Flex";
import { Link } from "lucide-react";
import { func, number, object, oneOfType, shape, string } from "prop-types";
import { useContext, useRef } from "react";
import { useSelector } from "react-redux";
import { selectIsEditing } from "slices/proposalOutline/selectors";

import RequirementChip from "./components/RequirementChip";
import { CHECKBOX_COLUMN_WIDTH } from "./constants";
import OutlineTableContext from "./OutlineTableContext";
import classes from "./SectionTitleCell.module.scss";
import { useLinkGrouping } from "./useLinkGrouping";
import { useRowThemeBackground } from "./useRowThemeBackground";

const SectionTitleCell = ({ getTable, getValue, row }) => {
  const isEditing = useSelector(selectIsEditing);
  const { documentId: hoveredDocumentId, rowId: hoveredRowId } =
    useContext(OutlineTableContext) ?? {};
  const rowRef = useRef(null);

  const { nextgenDocumentId, nextgenFileName } = row.original;

  const grouping = useLinkGrouping(row, nextgenDocumentId, getTable);

  const showDocumentChip = grouping.isLinked && grouping.isFirstInGroup;

  const isGroupHovered =
    grouping.isLinked &&
    hoveredDocumentId != null &&
    hoveredDocumentId === nextgenDocumentId;

  useRowThemeBackground(
    rowRef,
    isGroupHovered,
    grouping.documentTheme,
    `${hoveredDocumentId}:${hoveredRowId}`
  );

  return (
    <Flex
      alignItems={ALIGN_CLASS.center}
      className={classes.section_title}
      gap={GAP_CLASS.GAP_200}
      ref={rowRef}
      style={{
        "--checkbox-column-offset": isEditing
          ? `${CHECKBOX_COLUMN_WIDTH}px`
          : "0px",
        "--outline-theme-rail": grouping.documentTheme.rail,
      }}
    >
      {grouping.isLinked && (
        <div
          className={clsx(classes.link_connector, {
            [classes["link_connector--first"]]: grouping.isFirstInGroup,
            [classes["link_connector--last"]]: grouping.isLastInGroup,
          })}
          data-testid="link-connector"
        />
      )}

      <Flex className={classes.section_title__text}>
        <Ellipsis enableTooltip text={getValue()} />
      </Flex>

      {showDocumentChip && (
        <RequirementChip
          className={classes.section_title__document_chip}
          icon={<Link size={12} />}
          value={
            <Ellipsis enableTooltip maxWidth="100px" text={nextgenFileName} />
          }
        />
      )}
    </Flex>
  );
};

SectionTitleCell.propTypes = {
  getTable: func,
  getValue: func,
  row: shape({
    getCanExpand: func,
    getIsExpanded: func,
    id: oneOfType([number, string]),
    index: number,
    original: object,
  }),
};

export default SectionTitleCell;
